/**
 * Trade Route Cost Calculations
 *
 * Calculates the cost of building trade route segments
 * based on distance from Kingdom and tech bonuses.
 */

import { idToCoords, hexDistance } from '../../utils/hexGrid';
import { TRADE_ROUTES } from '../../data/world-map/balance';

/**
 * Calculate the cost of building a trade route segment on a tile
 *
 * @param {string} targetNodeId - Target node ID
 * @param {string[]} researchedTechs - Array of researched tech IDs
 * @returns {Object|null} Cost object { wood, stone, gold } or null if invalid
 */
export function calculateTradeRouteCost(targetNodeId, researchedTechs = []) {
  const coords = idToCoords(targetNodeId);
  if (!coords) return null;

  // Calculate hex distance from kingdom (0,0)
  const distance = hexDistance(0, 0, coords.q, coords.r);

  // Calculate multiplier based on distance
  // Formula: baseCost × (1 + distanceMultiplier × distance)
  const distanceMultiplier = 1 + (TRADE_ROUTES.distanceMultiplier * distance);
  const clampedMultiplier = Math.min(distanceMultiplier, TRADE_ROUTES.maxMultiplier);

  // Apply tech bonuses (reduce cost)
  let techReduction = 0;
  if (TRADE_ROUTES.techBonuses) {
    Object.entries(TRADE_ROUTES.techBonuses).forEach(([techId, bonus]) => {
      if (researchedTechs.includes(techId)) {
        techReduction += bonus;
      }
    });
  }

  // Calculate final tech multiplier (never below minimum)
  const techMultiplier = Math.max(
    TRADE_ROUTES.minCostMultiplier,
    1 - techReduction
  );

  // Calculate final cost
  const cost = {};
  Object.entries(TRADE_ROUTES.baseCost).forEach(([resource, baseAmount]) => {
    cost[resource] = Math.floor(baseAmount * clampedMultiplier * techMultiplier);
  });

  return cost;
}

/**
 * Get the distance multiplier for a tile (for UI display)
 *
 * @param {string} targetNodeId - Target node ID
 * @returns {number} Distance multiplier (1.0 = base cost)
 */
export function getDistanceMultiplier(targetNodeId) {
  const coords = idToCoords(targetNodeId);
  if (!coords) return 1.0;

  const distance = hexDistance(0, 0, coords.q, coords.r);
  return Math.min(
    1 + (TRADE_ROUTES.distanceMultiplier * distance),
    TRADE_ROUTES.maxMultiplier
  );
}

/**
 * Get the tech discount percentage (for UI display)
 *
 * @param {string[]} researchedTechs - Array of researched tech IDs
 * @returns {number} Discount percentage (0-1)
 */
export function getTechDiscount(researchedTechs = []) {
  let discount = 0;
  if (TRADE_ROUTES.techBonuses) {
    Object.entries(TRADE_ROUTES.techBonuses).forEach(([techId, bonus]) => {
      if (researchedTechs.includes(techId)) {
        discount += bonus;
      }
    });
  }
  return Math.min(discount, 1 - TRADE_ROUTES.minCostMultiplier);
}
